import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';

// The Tiptap whitelist enforced server-side (resource_api/core/configuration/
// tiptap_schema.py) restricts nodes to doc/paragraph/text/image/hardBreak and
// marks to bold/italic/underline/textStyle{color,fontSize}. Because the
// backend rejects any document outside this set on PATCH, the rendered HTML
// is safe to inject via dangerouslySetInnerHTML at the call site.
const EXTENSIONS = [
  StarterKit,
  Image,
  TextStyle,
];

type TiptapNode = { type?: string; text?: string; content?: TiptapNode[] };

// Tiptap's generateHTML throws "Empty text nodes are not allowed" on any text
// node whose text is the empty string. The owner announcement editor emits such
// a node for a title-only announcement, so drop empty text nodes before
// rendering. (SC-105)
function stripEmptyTextNodes(node: TiptapNode): TiptapNode {
  if (!node.content) return node;
  return {
    ...node,
    content: node.content
      .filter((child) => !(child.type === 'text' && (child.text ?? '') === ''))
      .map(stripEmptyTextNodes),
  };
}

// True when the document holds something worth rendering (any non-empty text or
// an image). Empty text nodes are assumed already stripped.
function hasRenderableContent(node: TiptapNode): boolean {
  if (node.type === 'text') return true;
  if (node.type === 'image') return true;
  return (node.content ?? []).some(hasRenderableContent);
}

export function renderTiptapToHtml(doc: unknown): string {
  if (!doc || typeof doc !== 'object') return '';
  const root = doc as TiptapNode;
  if (root.type !== 'doc') return '';
  if (!root.content || root.content.length === 0) return '';
  const cleaned = stripEmptyTextNodes(root);
  if (!hasRenderableContent(cleaned)) return '';
  // generateHTML can still throw on an unexpected/malformed document; a bad
  // announcement must never 404 the order site, so fall back to no body.
  try {
    return generateHTML(cleaned as Parameters<typeof generateHTML>[0], EXTENSIONS);
  } catch {
    return '';
  }
}
